/**
 * 🔄 LOADING SERVICE - SISTEMA CENTRALIZADO OTIMIZADO
 *
 * Sistema moderno de loading states que substitui completamente
 * qualquer dependência do Firebase, oferecendo:
 *
 * ✅ Estados globais e locais
 * ✅ Debounce automático
 * ✅ Interceptors Axios integrados
 * ✅ Contextos nomeados
 * ✅ Performance otimizada
 * ✅ Feedback visual consistente
 *
 * @author Sistema de Migração PostgreSQL
 * @version 2.0.0
 */

// import Vue from 'vue' // Removido: não utilizado diretamente

class LoadingService {
  constructor() {
    // 📊 Estados de loading organizados por contexto
    this.states = new Map()

    // ⏱️ Timers para debounce
    this.timers = new Map()

    // 🎯 Configurações padrão
    this.config = {
      debounceDelay: 100, // ms - evita flickering
      minDuration: 200, // ms - duração mínima visível
      maxDuration: 300000, // ms - timeout automático
      globalKey: '__global__'
    }

    // 📈 Métricas de performance
    this.metrics = {
      totalStarts: 0,
      totalStops: 0,
      averageDuration: 0,
      contexts: new Map()
    }

    // 🔧 Inicializar estado global
    this.initializeGlobalState()

    // 🎨 Configurar estilos CSS dinâmicos
    this.injectStyles()

    console.log('🔄 LoadingService: Inicializado com sucesso')
  }

  /**
   * 🌐 Inicializar estado global do Vuex
   */
  initializeGlobalState() {
    this.states.set(this.config.globalKey, {
      active: false,
      count: 0,
      contexts: new Set(),
      startTime: null,
      progress: 0
    })
  }

  /**
   * 🎨 Injetar estilos CSS para loading states
   */
  injectStyles() {
    const styles = `
      .loading-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        backdrop-filter: blur(2px);
        transition: opacity 0.2s ease;
      }
      
      .loading-spinner {
        animation: spin 1s linear infinite;
      }
      
      .loading-pulse {
        animation: pulse 1.5s ease-in-out infinite;
      }
      
      .loading-dots::after {
        content: '';
        animation: dots 1.5s steps(4, end) infinite;
      }
      
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
      
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
      
      @keyframes dots {
        0%, 20% { content: ''; }
        40% { content: '.'; }
        60% { content: '..'; }
        80%, 100% { content: '...'; }
      }
      
      .btn-loading {
        pointer-events: none;
        opacity: 0.7;
      }
      
      .table-loading {
        position: relative;
        overflow: hidden;
      }
      
      .table-loading::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: linear-gradient(90deg, transparent, #ffc107, transparent);
        animation: loading-bar 2s ease-in-out infinite;
      }
      
      @keyframes loading-bar {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(100%); }
      }
    `

    const styleSheet = document.createElement('style')
    styleSheet.textContent = styles
    document.head.appendChild(styleSheet)
  }

  /**
   * ▶️ Iniciar loading para um contexto
   * @param {string} context - Nome do contexto (ex: 'users', 'customers')
   * @param {Object} options - Opções de configuração
   */
  start(context = this.config.globalKey, options = {}) {
    const {
      debounce = true,
      // minDuration = this.config.minDuration, // Removido: não utilizado
      message = 'Carregando...',
      progress = false
    } = options

    // 📊 Atualizar métricas
    this.metrics.totalStarts++
    if (!this.metrics.contexts.has(context)) {
      this.metrics.contexts.set(context, {
        starts: 0,
        stops: 0,
        totalDuration: 0
      })
    }
    this.metrics.contexts.get(context).starts++

    // ⏱️ Aplicar debounce se necessário
    if (debounce && this.timers.has(`start_${context}`)) {
      clearTimeout(this.timers.get(`start_${context}`))
    }

    const startFn = () => {
      // 📝 Obter ou criar estado do contexto
      let state = this.states.get(context)
      if (!state) {
        state = {
          active: false,
          count: 0,
          contexts: new Set(),
          startTime: null,
          progress: 0,
          message
        }
        this.states.set(context, state)
      }

      // ▶️ Ativar loading
      state.count++
      state.active = true
      state.startTime = Date.now()
      state.message = message
      state.progress = progress ? 0 : null

      // 🌐 Atualizar estado global se necessário
      if (context !== this.config.globalKey) {
        const globalState = this.states.get(this.config.globalKey)
        globalState.contexts.add(context)
        globalState.active = globalState.contexts.size > 0
        globalState.count = Array.from(globalState.contexts).reduce(
          (sum, ctx) => {
            const ctxState = this.states.get(ctx)
            return sum + (ctxState ? ctxState.count : 0)
          },
          0
        )
      }

      // 📢 Emitir evento para componentes Vue
      this.emit('loading:start', { context, state: { ...state } })

      // ⏰ Timeout automático
      const timeoutKey = `timeout_${context}`
      if (this.timers.has(timeoutKey)) {
        clearTimeout(this.timers.get(timeoutKey))
      }

      this.timers.set(
        timeoutKey,
        setTimeout(() => {
          console.warn(`⚠️ Loading timeout para contexto: ${context}`)
          this.stop(context, { force: true })
        }, this.config.maxDuration)
      )

      // console.log(`▶️ Loading started: ${context}`, state)
    }

    if (debounce) {
      this.timers.set(
        `start_${context}`,
        setTimeout(startFn, this.config.debounceDelay)
      )
    } else {
      startFn()
    }

    return this
  }

  /**
   * ⏹️ Parar loading para um contexto
   * @param {string} context - Nome do contexto
   * @param {Object} options - Opções de configuração
   */
  stop(context = this.config.globalKey, options = {}) {
    const { debounce = true, force = false, delay = 0 } = options

    // ⏱️ Aplicar debounce se necessário
    if (debounce && this.timers.has(`stop_${context}`) && !force) {
      clearTimeout(this.timers.get(`stop_${context}`))
    }

    const stopFn = () => {
      const state = this.states.get(context)
      if (!state) return

      // ⏹️ Decrementar contador
      state.count = Math.max(0, state.count - 1)

      // 🛑 Desativar se contador chegou a zero
      if (state.count === 0 || force) {
        const duration = state.startTime ? Date.now() - state.startTime : 0

        // 📊 Atualizar métricas
        this.metrics.totalStops++
        if (this.metrics.contexts.has(context)) {
          const contextMetrics = this.metrics.contexts.get(context)
          contextMetrics.stops++
          contextMetrics.totalDuration += duration
        }

        // 🕐 Garantir duração mínima se não for forçado
        const remainingTime = force
          ? 0
          : Math.max(0, this.config.minDuration - duration)

        setTimeout(() => {
          state.active = false
          state.count = 0
          state.startTime = null
          state.progress = 0

          // 🌐 Atualizar estado global
          if (context !== this.config.globalKey) {
            const globalState = this.states.get(this.config.globalKey)
            globalState.contexts.delete(context)
            globalState.active = globalState.contexts.size > 0
            globalState.count = Array.from(globalState.contexts).reduce(
              (sum, ctx) => {
                const ctxState = this.states.get(ctx)
                return sum + (ctxState ? ctxState.count : 0)
              },
              0
            )
          }

          // 📢 Emitir evento
          this.emit('loading:stop', { context, state: { ...state }, duration })

          // 🧹 Limpar timers
          const timeoutKey = `timeout_${context}`
          if (this.timers.has(timeoutKey)) {
            clearTimeout(this.timers.get(timeoutKey))
            this.timers.delete(timeoutKey)
          }

          // console.log(`⏹️ Loading stopped: ${context} (${duration}ms)`)
        }, remainingTime)
      }
    }

    if (debounce && !force) {
      this.timers.set(
        `stop_${context}`,
        setTimeout(stopFn, this.config.debounceDelay)
      )
    } else {
      setTimeout(stopFn, delay)
    }

    return this
  }

  /**
   * 📈 Atualizar progresso de um loading
   * @param {string} context - Nome do contexto
   * @param {number} progress - Progresso (0-100)
   */
  setProgress(context, progress) {
    const state = this.states.get(context)
    if (state && state.active) {
      state.progress = Math.max(0, Math.min(100, progress))
      this.emit('loading:progress', { context, progress: state.progress })
    }
    return this
  }

  /**
   * ❓ Verificar se contexto está carregando
   * @param {string} context - Nome do contexto
   * @returns {boolean}
   */
  isLoading(context = this.config.globalKey) {
    const state = this.states.get(context)
    return state ? state.active : false
  }

  /**
   * 📊 Obter estado de um contexto
   * @param {string} context - Nome do contexto
   * @returns {Object}
   */
  getState(context = this.config.globalKey) {
    const state = this.states.get(context)
    return state ? { ...state } : null
  }

  /**
   * 📈 Obter todas as métricas
   * @returns {Object}
   */
  getMetrics() {
    // 🧮 Calcular médias
    const totalDuration = Array.from(this.metrics.contexts.values()).reduce(
      (sum, ctx) => sum + ctx.totalDuration,
      0
    )

    this.metrics.averageDuration =
      this.metrics.totalStops > 0
        ? Math.round(totalDuration / this.metrics.totalStops)
        : 0

    return {
      ...this.metrics,
      contexts: Object.fromEntries(this.metrics.contexts)
    }
  }

  /**
   * 🧹 Limpar todos os estados
   */
  clear() {
    // 🛑 Parar todos os loadings
    for (const context of this.states.keys()) {
      this.stop(context, { force: true })
    }

    // 🧹 Limpar timers
    for (const timer of this.timers.values()) {
      clearTimeout(timer)
    }
    this.timers.clear()

    // 🔄 Reinicializar
    this.states.clear()
    this.initializeGlobalState()

    console.log('🧹 LoadingService: Estados limpos')
    return this
  }

  /**
   * 📢 Emitir evento personalizado
   * @param {string} event - Nome do evento
   * @param {Object} data - Dados do evento
   */
  emit(event, data) {
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(
        new CustomEvent(`loadingService:${event}`, {
          detail: data
        })
      )
    }
  }

  /**
   * 🎯 Configurar interceptors do Axios
   * @param {Object} axiosInstance - Instância do Axios
   */
  setupAxiosInterceptors(axiosInstance) {
    // 📤 Request interceptor
    axiosInstance.interceptors.request.use(
      (config) => {
        const context = config.loadingContext || 'api'
        const options = {
          message: config.loadingMessage || 'Carregando...',
          debounce: config.loadingDebounce !== false
        }

        // ▶️ Iniciar loading automaticamente
        if (config.loading !== false) {
          this.start(context, options)
        }

        // 🏷️ Marcar requisição com contexto
        config._loadingContext = context

        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // 📥 Response interceptor
    axiosInstance.interceptors.response.use(
      (response) => {
        const context = response.config._loadingContext
        if (context && response.config.loading !== false) {
          this.stop(context)
        }
        return response
      },
      (error) => {
        const context = error.config?._loadingContext
        if (context && error.config?.loading !== false) {
          this.stop(context, { force: true })
        }
        return Promise.reject(error)
      }
    )

    console.log('🔗 LoadingService: Interceptors Axios configurados')
    return this
  }
}

// 🌟 Instância singleton
const loadingService = new LoadingService()

// 🔌 Plugin Vue
const LoadingPlugin = {
  install(Vue) {
    // 💉 Injetar no prototype do Vue
    Vue.prototype.$loading = loadingService

    // 🎯 Mixin global para facilitar uso
    Vue.mixin({
      data() {
        return {
          loadingStates: {}
        }
      },

      methods: {
        /**
         * 🚀 Wrapper para operações assíncronas com loading
         * @param {Function} operation - Operação assíncrona
         * @param {string} context - Contexto do loading
         * @param {Object} options - Opções
         */
        async withLoading(operation, context = 'default', options = {}) {
          try {
            this.$loading.start(context, options)
            const result = await operation()
            return result
          } catch (error) {
            console.error(`Erro em ${context}:`, error)
            throw error
          } finally {
            this.$loading.stop(context)
          }
        },

        /**
         * 📊 Obter estado de loading reativo
         * @param {string} context - Contexto
         * @returns {Object}
         */
        getLoadingState(context) {
          if (!this.loadingStates[context]) {
            Vue.set(this.loadingStates, context, {
              active: false,
              progress: 0,
              message: ''
            })
          }
          return this.loadingStates[context]
        }
      },

      created() {
        // 🎧 Escutar eventos de loading
        const updateState = (event) => {
          const { context, state } = event.detail
          if (this.loadingStates[context]) {
            Object.assign(this.loadingStates[context], state)
          }
        }

        window.addEventListener('loadingService:loading:start', updateState)
        window.addEventListener('loadingService:loading:stop', updateState)
        window.addEventListener('loadingService:loading:progress', updateState)

        // 🧹 Cleanup
        this.$once('hook:beforeDestroy', () => {
          window.removeEventListener(
            'loadingService:loading:start',
            updateState
          )
          window.removeEventListener(
            'loadingService:loading:stop',
            updateState
          )
          window.removeEventListener(
            'loadingService:loading:progress',
            updateState
          )
        })
      }
    })

    // 🌐 Adicionar ao Vuex se disponível
    if (Vue.prototype.$store) {
      // 🔒 Verificar se o módulo já existe antes de registrar
      const store = Vue.prototype.$store
      const moduleName = 'loading'

      // Se o módulo já existe, removê-lo primeiro para evitar conflitos
      if (store.hasModule(moduleName)) {
        store.unregisterModule(moduleName)
      }

      store.registerModule(moduleName, {
        namespaced: true,
        state: () => ({
          global: false,
          contexts: {}
        }),
        mutations: {
          SET_GLOBAL(state, active) {
            // 🔒 Validar que state é um objeto antes de modificar
            if (state && typeof state === 'object' && !Array.isArray(state)) {
              state.global = active
            }
          },
          SET_CONTEXT(state, { context, active }) {
            // 🔒 Validar que state é um objeto válido
            if (!state || typeof state !== 'object' || Array.isArray(state)) {
              console.warn('⚠️ SET_CONTEXT: state inválido', state)
              return
            }

            // 🔒 Garantir que state.contexts sempre exista e seja um objeto
            if (!state.contexts || typeof state.contexts !== 'object' || Array.isArray(state.contexts)) {
              state.contexts = {}
            }

            // Verificar se o context é válido
            if (context && typeof context === 'string') {
              Vue.set(state.contexts, context, active)
            }
          }
        },
        getters: {
          isGlobalLoading: (state) => state.global,
          isContextLoading: (state) => (context) => {
            return state.contexts && !!state.contexts[context]
          },
          activeContexts: (state) => {
            if (!state.contexts) return []
            return Object.keys(state.contexts).filter((ctx) => state.contexts[ctx])
          }
        }
      })

      // 🔄 Sincronizar com Vuex
      window.addEventListener('loadingService:loading:start', (event) => {
        try {
          // 🔒 Validar que o store está disponível e o módulo está registrado
          if (!store || !store.state || !store.hasModule(moduleName)) {
            return
          }

          // 🔒 Acessar o estado do módulo namespaced corretamente
          const loadingState = store.state[moduleName]

          // 🔒 Validar que o estado do módulo é um objeto válido (não booleano)
          if (!loadingState || typeof loadingState !== 'object' || Array.isArray(loadingState)) {
            // Se não for um objeto válido, o módulo pode não estar inicializado corretamente
            // Não mostrar warning, apenas retornar silenciosamente
            return
          }

          const { context } = event.detail || {}
          if (!context || typeof context !== 'string') {
            return
          }

          store.commit('loading/SET_CONTEXT', {
            context,
            active: true
          })
          if (context !== '__global__') {
            store.commit('loading/SET_GLOBAL', true)
          }
        } catch (error) {
          // Silenciar erros para não poluir o console
          // console.warn('⚠️ Erro ao sincronizar loading start com Vuex:', error)
        }
      })

      window.addEventListener('loadingService:loading:stop', (event) => {
        try {
          // 🔒 Validar que o store está disponível e o módulo está registrado
          if (!store || !store.state || !store.hasModule(moduleName)) {
            return
          }

          // 🔒 Acessar o estado do módulo namespaced corretamente
          const loadingState = store.state[moduleName]

          // 🔒 Validar que o estado do módulo é um objeto válido (não booleano)
          if (!loadingState || typeof loadingState !== 'object' || Array.isArray(loadingState)) {
            // Se não for um objeto válido, o módulo pode não estar inicializado corretamente
            // Não mostrar warning, apenas retornar silenciosamente
            return
          }

          const { context } = event.detail || {}
          if (!context || typeof context !== 'string') {
            return
          }

          store.commit('loading/SET_CONTEXT', {
            context,
            active: false
          })

          // Verificar se ainda há contextos ativos
          if (loadingState.contexts && typeof loadingState.contexts === 'object' && !Array.isArray(loadingState.contexts)) {
            const hasActiveContexts = Object.values(loadingState.contexts).some((active) => active === true)
            store.commit('loading/SET_GLOBAL', hasActiveContexts)
          } else {
            store.commit('loading/SET_GLOBAL', false)
          }
        } catch (error) {
          // Silenciar erros para não poluir o console
          // console.warn('⚠️ Erro ao sincronizar loading stop com Vuex:', error)
        }
      })
    }
  }
}

export default loadingService
export { LoadingPlugin }
